/** Jarvis Knowledge — test a question (optionally as another role) and inspect the retrieved sources. */
import React, { useState } from 'react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { StatusPill, btnPrimary, errorText, inputCls } from '../finance/financeUi';
import { ROLES } from './PackList';

export const sourceLine = (s) => `${s.title} — ${s.pack}${s.page ? `, p. ${s.page}` : ''}${s.section ? `, “${s.section}”` : ''}${s.captured_at ? `, captured ${new Date(s.captured_at).toLocaleDateString()}` : ''} (v${s.version})`;

const Rich = ({ text }) => (
  <p className="whitespace-pre-wrap" data-testid="jarvis-test-answer">
    {text.split(/(\*\*[^*]+\*\*)/g).map((part, i) => (part.startsWith('**') && part.endsWith('**') ? <strong key={i}>{part.slice(2, -2)}</strong> : part))}
  </p>
);

export function TestBox() {
  const [q, setQ] = useState('');
  const [role, setRole] = useState('super_admin');
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { setRes(await api.request('/jarvis/test', { method: 'POST', body: JSON.stringify({ question: q, as_role: role }) })); } catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3 px-5 py-4" data-testid="jarvis-test-box">
      <div className="flex flex-wrap gap-2">
        <input className={`${inputCls} max-w-xl`} placeholder="e.g. How do I enable tipping on the Clover Flex?" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && q.trim() && run()} data-testid="jarvis-test-question" />
        <select className={`${inputCls} max-w-[11rem]`} value={role} onChange={(e) => setRole(e.target.value)} data-testid="jarvis-test-role">
          <option value="super_admin">As Super Admin</option>
          {ROLES.map(([r, l]) => <option key={r} value={r}>As {l.replace(/s$/, '')}</option>)}
        </select>
        <button type="button" className={btnPrimary} disabled={!q.trim() || busy} onClick={run} data-testid="jarvis-test-btn">{busy ? 'Asking…' : 'Test'}</button>
      </div>
      {res && (
        <div className="space-y-3 rounded-lg border p-4 text-sm" data-testid="jarvis-test-result">
          <div className="flex flex-wrap gap-2">
            <StatusPill value={res.answer_mode} testid="jarvis-test-mode" />
            <StatusPill value={`EVIDENCE_${res.evidence}`} testid="jarvis-test-evidence" />
            {res.grounding_required && <StatusPill value="NEEDS_MSC_SOURCE" testid="jarvis-test-grounding" />}
          </div>
          <Rich text={res.response || ''} />
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Retrieved sources</p>
            {res.sources.length ? (
              <ol className="mt-1 space-y-2" data-testid="jarvis-test-sources">
                {res.sources.map((s) => (
                  <li key={s.n} className="rounded-md bg-muted/40 px-3 py-2" data-testid={`jarvis-test-source-${s.n}`}>
                    <p className="font-medium">{s.n}. {sourceLine(s)} · match {s.evidence}</p>
                    <p className="text-xs text-muted-foreground">{s.excerpt}…</p>
                  </li>
                ))}
              </ol>
            ) : <p className="text-xs text-muted-foreground" data-testid="jarvis-test-no-sources">No authorized passage matched.</p>}
          </div>
        </div>
      )}
    </div>
  );
}
